import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptSimplifier } from "meshoptimizer/simplifier";
import { PRIZE_SHOT_COIN_URL } from "./prize_shot_coin_art.js";

const SHELF_MODEL_URL = "assets/models/events/nightstand.glb?v=1";
const BALLOON_MODEL_URL = "assets/models/events/ball.glb?v=1";
const SHELF_MAP_URL = "assets/textures/prize_event_shelve.png?v=1";
const SHELF_NORMAL_URL = "assets/textures/prize_event_shelve_normal.png?v=1";
const FLOOR_URL = "assets/textures/prize_shot_background.jpg?v=1";
const STAGE_URL = "assets/textures/prize_shot_top.png?v=1";
const TENT_URL = "assets/textures/prize_shot_sky.png?v=1";

/** Mesh names inside the GLBs; the files also carry reference blocks for scale. */
const SHELF_MESH = "nightstand";
const BALLOON_MESH = "ball.002";
/** Share of ball.glb's triangles the balloons keep. */
const BALLOON_TRIANGLE_SHARE = 0.25;

export interface PrizeShotAssets
{
  shelf_geometry: THREE.BufferGeometry;
  balloon_geometry: THREE.BufferGeometry;
  shelf_map: THREE.Texture;
  shelf_normal: THREE.Texture;
  /** The circus for Ground: floor with the star and the ring, the stage behind it, the tent above. */
  floor: THREE.Texture;
  stage: THREE.Texture;
  tent: THREE.Texture;
  coins: THREE.Texture;
}

/**
 * The Prize Shot models and textures are only needed inside the event, so they
 * are not part of the startup ResourceManager set: they load ahead in the idle
 * lobby (PrizeShotScreen.prefetchAssets) or on the first entry, and stay cached
 * for the session.
 */
export class PrizeShotAssetLoader
{
  private static loading: Promise<PrizeShotAssets> | null = null;
  /** Per URL, so the lobby prefetch and the entry share one load. */
  private static textures = new Map<string, Promise<THREE.Texture>>();

  /** The pictures only, fetched and decoded off the main thread; see PrizeShotScreen.prefetchAssets. */
  public static async prefetchPictures(): Promise<void>
  {
    await Promise.allSettled( [
      ...[ FLOOR_URL, STAGE_URL, TENT_URL ].map( url => this.texture( url, true, true ) ),
      ...[ SHELF_MAP_URL, PRIZE_SHOT_COIN_URL ].map( url => this.texture( url, true ) ),
      this.texture( SHELF_NORMAL_URL, false ),
    ] );
  }

  private static texture( url: string, srgb: boolean, as_bitmap = false ): Promise<THREE.Texture>
  {
    let loading = this.textures.get( url );
    if ( !loading )
    {
      loading = as_bitmap ? this.loadBitmapTexture( url ) : this.loadTexture( url, srgb );
      loading.catch( () => this.textures.delete( url ) );
      this.textures.set( url, loading );
    }
    return loading;
  }

  public static load(): Promise<PrizeShotAssets>
  {
    if ( !this.loading )
    {
      this.loading = this.loadAll();
      this.loading.catch( () => { this.loading = null; } );
    }
    return this.loading;
  }

  private static async loadAll(): Promise<PrizeShotAssets>
  {
    const [ shelf, balloon, shelf_map, shelf_normal, floor, stage, tent, coins ] = await Promise.all( [
      this.loadMesh( SHELF_MODEL_URL, SHELF_MESH ),
      this.loadMesh( BALLOON_MODEL_URL, BALLOON_MESH ),
      this.texture( SHELF_MAP_URL, true ),
      this.texture( SHELF_NORMAL_URL, false ),
      this.texture( FLOOR_URL, true, true ),
      this.texture( STAGE_URL, true, true ),
      this.texture( TENT_URL, true, true ),
      this.texture( PRIZE_SHOT_COIN_URL, true ),
    ] );

    // glTF UVs run top-down, the textures are authored for them.
    shelf_map.flipY = false;
    shelf_normal.flipY = false;
    shelf_map.anisotropy = 4;

    await this.simplifyBalloon( balloon.geometry );

    return {
      shelf_geometry: shelf.geometry,
      balloon_geometry: balloon.geometry,
      shelf_map,
      shelf_normal,
      floor,
      stage,
      tent,
      coins,
    };
  }

  /**
   * ball.glb is 13 632 triangles: 20 of them, drawn again for the shadow, were
   * ~40% of the GPU frame. At shelf size a quarter of that differs only on
   * outline pixels (checked against the full mesh, user-approved 2026-10-26).
   * Only the index buffer changes; on any failure the full mesh stays.
   */
  private static async simplifyBalloon( geometry: THREE.BufferGeometry ): Promise<void>
  {
    const position = geometry.getAttribute( "position" );
    const normal = geometry.getAttribute( "normal" );
    if ( !geometry.index || !position || !normal )
      return;

    try
    {
      await MeshoptSimplifier.ready;
      if ( !MeshoptSimplifier.supported )
        return;

      const indices = new Uint32Array( geometry.index.array );
      const target = Math.floor( indices.length * BALLOON_TRIANGLE_SHARE / 3 ) * 3;
      const [ simplified ] = MeshoptSimplifier.simplifyWithAttributes(
        indices,
        new Float32Array( position.array ),
        3,
        new Float32Array( normal.array ),
        3,
        [ 0.5, 0.5, 0.5 ],
        null,
        target,
        0.01,
        []
      );

      if ( simplified.length > 0 && simplified.length < indices.length )
        geometry.setIndex( new THREE.BufferAttribute( position.count > 0xffff ? simplified : new Uint16Array( simplified ), 1 ) );
    }
    catch ( e )
    {
      console.warn( "[PrizeShot] balloon simplification failed, the full mesh is used", e );
    }
  }

  private static loadMesh( url: string, mesh_name: string ): Promise<THREE.Mesh>
  {
    return new Promise( ( resolve, reject ) =>
    {
      new GLTFLoader().load( url, gltf =>
      {
        // GLTFLoader strips dots from node names ("ball.002" → "ball002").
        const mesh = gltf.scene.getObjectByName( THREE.PropertyBinding.sanitizeNodeName( mesh_name ) ) as THREE.Mesh | undefined;
        if ( !mesh?.isMesh )
        {
          reject( new Error( `[PrizeShot] ${url} has no mesh "${mesh_name}"` ) );
          return;
        }
        resolve( mesh );
      }, undefined, reject );
    } );
  }

  /**
   * The 1800 px circus pictures (Ground draws them flipped, flipY). From an
   * <img> the first upload decodes, flips and converts them on the main thread
   * (~0.5 s of a frozen first entry at CPU x4), and createImageBitmap(<img>)
   * also decodes there (~100 ms each on the phone). From a Blob it decodes and
   * flips off the main thread, and the upload is a plain copy. WebGL ignores
   * the unpack flags for an ImageBitmap, so it is made the way they would have
   * unpacked the <img>: flipped, alpha not premultiplied, no colour conversion
   * (sRGB on the sRGB working space). Same pixels; checked with a pixel diff.
   */
  private static async loadBitmapTexture( url: string ): Promise<THREE.Texture>
  {
    if ( typeof createImageBitmap !== "function" || typeof fetch !== "function" )
      return this.loadTexture( url, true );

    try
    {
      const response = await fetch( url );
      if ( !response.ok )
        throw new Error( `${url}: ${response.status}` );
      const bitmap = await createImageBitmap( await response.blob(), {
        imageOrientation: "flipY",
        premultiplyAlpha: "none",
        colorSpaceConversion: "none",
      } );

      const texture = new THREE.Texture( bitmap as unknown as HTMLImageElement );
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.flipY = true;
      texture.needsUpdate = true;
      return texture;
    }
    catch ( e )
    {
      console.warn( `[PrizeShot] ${url} as a bitmap failed, loading it as an image`, e );
      return this.loadTexture( url, true );
    }
  }

  /** Decoded ahead (img.decode, off the main thread), so its first upload does not decode it. */
  private static loadTexture( url: string, srgb: boolean ): Promise<THREE.Texture>
  {
    return new Promise( ( resolve, reject ) =>
    {
      new THREE.TextureLoader().load( url, texture =>
      {
        texture.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
        const image = texture.image as HTMLImageElement;
        const decoded = typeof image?.decode === "function" ? image.decode() : Promise.resolve();
        decoded.catch( () => {} ).then( () => resolve( texture ) );
      }, undefined, reject );
    } );
  }
}
